// Dev Mode "compare changes": when a frame is marked ready for dev, a snapshot of it is kept on
// the frame. Later, the same three-way diff branches use shows what changed since then.

import type { Document } from './document.ts';
import type { NodeData, NodeId } from './types.ts';
import { planMerge, type MergeChange } from './merge.ts';

/** Keys that track the handoff itself (or where the frame sits on the canvas), not its design. */
const IGNORE_ON_ROOT = ['devStatus', 'devSnapshot', 'x', 'y', 'parent', 'index'] as const;

/** The frame and its layers, as they are now, for the snapshot. */
export function readySnapshot(doc: Document, id: NodeId): NodeData[] {
  return doc.subtree(id).map((n) => {
    const c = structuredClone(n);
    delete c.devSnapshot;
    return c;
  });
}

function strip(nodes: NodeData[], rootId: NodeId): NodeData[] {
  return nodes.map((n) => {
    if (n.id !== rootId) return n;
    const c = { ...n } as Record<string, unknown>;
    for (const k of IGNORE_ON_ROOT) delete c[k];
    return c as unknown as NodeData;
  });
}

/** What changed in a ready frame since it was marked ready (null: it has no snapshot). */
export function changesSinceReady(doc: Document, id: NodeId): MergeChange[] | null {
  const n = doc.get(id);
  if (!n?.devSnapshot) return null;
  const before = { format: 'vellum', version: 1, nodes: strip(n.devSnapshot.nodes, id) } as const;
  const now = { format: 'vellum', version: 1, nodes: strip(readySnapshot(doc, id), id) } as const;
  return planMerge(before, before, now).changes;
}
